<script setup lang="ts">
import { parseColorsParam } from "~/utils/colorBuckets";
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick, defineAsyncComponent } from "vue";
import { useQuery } from "@tanstack/vue-query";
import type { Database } from "~/types/database.types";
import { getArtworkImageSrc, getFramedArtworkImageSrc, convertInchesToCm, formatFramingCost, parseFramingCost } from "~/utils";
import CustomLoader from "~/components/ui/CustomLoader.vue";
import TextInput from "~/components/ui/TextInput.vue";
import SelectInput from "~/components/ui/SelectInput.vue";
import Button from "~/components/atoms/Button.vue";
import SectionHeader from "~/components/ui/SectionHeader.vue";
import CustomLink from "~/components/CustomLink.vue";
import ImageThatFadesIn from "~/components/ui/ImageThatFadesIn.vue";
import HeartLikeButton from "~/components/atoms/HeartLikeButton.vue";
import { refDebounced, useMediaQuery, onClickOutside } from "@vueuse/core";
import MediumFormattedText from "~/components/molecules/MediumFormattedText.vue";
import MeasurementUnitToggle from "~/components/molecules/MeasurementUnitToggle.vue";
import ArtworkPriceDisplay from "~/components/molecules/ArtworkPriceDisplay.vue";
import { UNAVAILABLE_ARTWORK_STATUSES, SOLD_ARTWORK_STATUSES } from "~/constants";
import type { ArtworkFilters } from "~/components/molecules/ArtworkFeed.vue";
import { useShippingAvailability } from "~/composables/useShippingAvailability";
import { calculateShippingCost, parseWeightKg, parseArtworkDimensions, validatePincodeWithState } from "~/utils/shippingCalculator";
import { stretcherBarDepthInches, resolveFramingArtworkType, type FramingMode } from "~/utils/framingCalculator";
import { buildPieceFraming, needsMountOrSideDepth, type ArtworkFramingRecord } from "~/utils/framingOrderPayload";
import { countries } from "~/utils/countries";
import { useArtBrowsingNudge } from "~/composables/useArtBrowsingNudge";
import { useAnalytics } from "~/composables/useAnalytics";

// Lazy-loaded heavy components (not needed for immediate hero destination snapshot).
// suspensible: false keeps them out of the page's <Suspense> boundary so a cold-cache
// chunk fetch can never delay `page:finish` (and therefore the view transition, which
// awaits it) — they mount on their own once loaded instead of blocking the whole page.
const ArtworkZoomModal = defineAsyncComponent({ loader: () => import("~/components/ui/ArtworkZoomModal.vue"), suspensible: false });
const FramingCalculator = defineAsyncComponent({ loader: () => import("~/components/internal/FramingCalculator.vue"), suspensible: false });
const FrameVisualizer = defineAsyncComponent({ loader: () => import("~/components/internal/FrameVisualizer.vue"), suspensible: false });
const ShippingBreakdownModal = defineAsyncComponent({ loader: () => import("~/components/ui/ShippingBreakdownModal.vue"), suspensible: false });
const SelectionRemovalConfirmationModal = defineAsyncComponent({ loader: () => import("~/components/molecules/SelectionRemovalConfirmationModal.vue"), suspensible: false });
const PriceOnRequestForm = defineAsyncComponent({ loader: () => import("~/components/ui/PriceOnRequestForm.vue"), suspensible: false });
const AddressSelector = defineAsyncComponent({ loader: () => import("~/components/ui/AddressSelector.vue"), suspensible: false });
const ShippingStatusBadge = defineAsyncComponent({ loader: () => import("~/components/ui/ShippingStatusBadge.vue"), suspensible: false });
import CountryCodeSelect from "~/components/ui/CountryCodeSelect.vue";
const ArtworkFeed = defineAsyncComponent({ loader: () => import("~/components/molecules/ArtworkFeed.vue"), suspensible: false });
const ArtworkSortFilterBar = defineAsyncComponent({ loader: () => import("~/components/molecules/ArtworkSortFilterBar.vue"), suspensible: false });
const BuybackSection = defineAsyncComponent({ loader: () => import("~/components/molecules/BuybackSection.vue"), suspensible: false });
const CurrentlyOnView = defineAsyncComponent({ loader: () => import("~/components/molecules/CurrentlyOnView.vue"), suspensible: false });

const {
  trackArtworkDetailViewed,
  trackArtworkSaved,
  trackArtworkShared,
  trackPurchaseCtaClicked,
  getCardAttribution,
} = useAnalytics();

const { recordInteraction } = useArtBrowsingNudge();
recordInteraction();

const runtimeConfig = useRuntimeConfig();
const isBuybackOpen = ref(false);
const isShippingBreakdownOpen = ref(false);
const canShowShippingBreakdown = computed(() => {
  return Boolean(
    import.meta.dev ||
    runtimeConfig.public.showShippingBreakdown ||
    runtimeConfig.public.nodeEnv === "development" ||
    runtimeConfig.public.appEnv === "staging" ||
    runtimeConfig.public.appEnv === "developement" ||
    runtimeConfig.public.appEnv === "development"
  );
});

definePageMeta({
  viewTransition: true,
});

const route = useRoute();
const router = useRouter();

const slug = route.params.slug as string;

const { getTransitionStyle, getHeroTransitionStyle, isArtworkActive, getPrefetchedArtwork, isTransitionActive } = useArtworkViewTransition();
const prefetchedArtwork = getPrefetchedArtwork(slug);

const appState = useAppState();
const { data: artwork, status } = useAsyncData(
  `artwork-${slug}`,
  async () => {
    return await $fetch<any>(`/api/artworks/${slug}`);
  },
  {
    lazy: true,
    default: () => prefetchedArtwork,
  }
);

const constellation = computed(() => artwork.value?.constellation);

watch(
  artwork,
  (art) => {
    if (art && art.id) {
      const attr = getCardAttribution();
      trackArtworkDetailViewed({
        artwork_id: art.id,
        artwork_slug: art.slug,
        artist_id: art.artist_id,
        price: art.listing_price || art.price,
        currency: "INR",
        availability: art.availability,
        entry_exposure_id: attr?.exposure_id,
        referrer_page: typeof document !== "undefined" ? document.referrer : undefined,
      });
    }
  },
  { immediate: true }
);

const canonicalUrl = computed(() => {
  if (!artwork.value) return `https://quietart.com/artworks/${slug}`;
  return `https://quietart.com/artworks/${artwork.value.slug || artwork.value.id}`;
});

const title = computed(() => {
  if (!artwork.value) return "Original Artwork | Quiet Art Movement";
  return `${artwork.value.name} by ${artwork.value.artist_name} - Original Artwork | Quiet Art Movement`;
});

const description = computed(() => {
  if (!artwork.value) return "Explore original contemporary artworks on Quiet Art Movement.";
  const details = [];
  if (artwork.value.medium) details.push(artwork.value.medium);
  if (artwork.value.year) details.push(artwork.value.year);
  if (artwork.value.display_size) details.push(artwork.value.display_size);
  const detailsStr = details.length > 0 ? ` (${details.join(', ')})` : '';
  return `Explore "${artwork.value.name}" by ${artwork.value.artist_name}${detailsStr}. Original artwork available at Quiet Art Movement.`;
});

const ogImage = computed(() => {
  if (!artwork.value?.photo_name) return undefined;
  return getArtworkImageSrc(artwork.value.photo_name, 720);
});

useSeoMeta({
  title: () => title.value,
  description: () => description.value,
  ogTitle: () => title.value,
  ogDescription: () => description.value,
  ogUrl: () => canonicalUrl.value,
  ogType: 'website',
  ogImage: () => ogImage.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title.value,
  twitterDescription: () => description.value,
  twitterImage: () => ogImage.value,
});

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: () => {
        if (!artwork.value) return '';
        let widthVal: string | undefined = undefined;
        let heightVal: string | undefined = undefined;
        if (artwork.value.size_in_inches) {
          const parts = artwork.value.size_in_inches.split(/x/i);
          if (parts.length >= 2) {
            widthVal = parts[0].trim();
            heightVal = parts[1].trim();
          }
        }
        return JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VisualArtwork",
          "name": artwork.value.name,
          "image": artwork.value.photo_name ? getArtworkImageSrc(artwork.value.photo_name, 1920) : undefined,
          "url": canonicalUrl.value,
          "creator": {
            "@type": "Person",
            "name": artwork.value.artist_name,
            "url": `https://quietart.com/artists/${artwork.value.artist_slug}`
          },
          "artMedium": artwork.value.medium || undefined,
          "dateCreated": artwork.value.year || undefined,
          "width": widthVal || undefined,
          "height": heightVal || undefined,
          "size": artwork.value.size_in_inches || undefined,
          "mainEntityOfPage": canonicalUrl.value
        });
      }
    },
    {
      type: 'application/ld+json',
      innerHTML: () => {
        if (!artwork.value) return '';
        return JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://quietart.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Artists",
              "item": "https://quietart.com/artists"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": artwork.value.artist_name,
              "item": `https://quietart.com/artists/${artwork.value.artist_slug}`
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": artwork.value.name,
              "item": canonicalUrl.value
            }
          ]
        });
      }
    }
  ]
});
function share() {
  if (artwork.value?.id) {
    trackArtworkShared({
      artwork_id: artwork.value.id,
      share_channel: "native_share",
      source_context: "artwork_detail",
    });
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    navigator.share({
      url: window.location.href,
      title: `${artwork.value?.name ?? ""} · Quiet Art Movement`,
    });
  }
}

const { toggleSavingArtwork } = useSelectionActions();
import { useRemovalConfirmation } from "~/composables/useRemovalConfirmation";

const { open, confirmRemoval, onConfirm, onCancel } = useRemovalConfirmation();

// --- "More by this artist" search, sort & filter state (restored from the
// URL so it survives navigating away and back) ---
const artworkSearchInput = ref((route.query.q as string) || "");
const debouncedArtworkSearch = refDebounced(artworkSearchInput, 400);

function parseArtworkFiltersFromQuery(query: typeof route.query): ArtworkFilters {
  const filters: ArtworkFilters = {};
  if (query.priceMin !== undefined) filters.priceMin = Number(query.priceMin);
  if (query.priceMax !== undefined) filters.priceMax = Number(query.priceMax);
  if (query.sizeMin !== undefined) filters.sizeMin = Number(query.sizeMin);
  if (query.sizeMax !== undefined) filters.sizeMax = Number(query.sizeMax);
  if (query.orientation) {
    filters.orientation = query.orientation as ArtworkFilters["orientation"];
  }
  if (query.medium) {
    const raw = Array.isArray(query.medium) ? query.medium[0] : query.medium;
    filters.medium = (raw as string).split(",").filter(Boolean);
  }
  const parsedColors = parseColorsParam(query.colors);
  if (parsedColors) filters.colors = parsedColors;
  if (query.excludeSold === "true") {
    filters.excludeSold = true;
  }
  if (query.fastDispatch === "true") {
    filters.fastDispatch = true;
  }
  if (query.onlySets === "true") {
    filters.onlySets = true;
  }
  return filters;
}

const artworkSortBy = ref<string | undefined>((route.query.sort as string) || undefined);
const artworkFilters = ref<ArtworkFilters>(parseArtworkFiltersFromQuery(route.query));

function syncArtworkQuery() {
  const query: Record<string, any> = { ...route.query };

  if (debouncedArtworkSearch.value) query.q = debouncedArtworkSearch.value;
  else delete query.q;

  if (artworkSortBy.value) query.sort = artworkSortBy.value;
  else delete query.sort;

  const f = artworkFilters.value;
  if (f.priceMin !== undefined) query.priceMin = String(f.priceMin);
  else delete query.priceMin;
  if (f.priceMax !== undefined) query.priceMax = String(f.priceMax);
  else delete query.priceMax;
  if (f.sizeMin !== undefined) query.sizeMin = String(f.sizeMin);
  else delete query.sizeMin;
  if (f.sizeMax !== undefined) query.sizeMax = String(f.sizeMax);
  else delete query.sizeMax;
  if (f.orientation) query.orientation = f.orientation;
  else delete query.orientation;
  if (f.medium && f.medium.length) query.medium = f.medium.join(",");
  else delete query.medium;
  if (f.colors && f.colors.length) query.colors = f.colors.join(",");
  else delete query.colors;
  if (f.excludeSold) query.excludeSold = "true";
  else delete query.excludeSold;
  if (f.fastDispatch) query.fastDispatch = "true";
  else delete query.fastDispatch;
  if (f.onlySets) query.onlySets = "true";
  else delete query.onlySets;

  router.replace({ query });
}

watch(debouncedArtworkSearch, syncArtworkQuery);

function onArtworkSortByUpdate(value: string | undefined) {
  artworkSortBy.value = value;
  syncArtworkQuery();
}

function onArtworkFiltersUpdate(value: ArtworkFilters) {
  artworkFilters.value = value;
  syncArtworkQuery();
}

const isArtworkSaved = computed(() =>
  appState.localSavedItems.value.has(artwork.value?.id ?? ""),
);

// --- PAYMENT LOGIC ---
const { processPayment } = usePayment();
const { isLoggedIn, profile, user } = useAuth();
const { addToCart, isAdding, cartItems, openCart } = useCart();

const isInCart = computed(() => cartItems.value.some(item => item.type === 'artwork' && item.id === artwork.value?.id));

const toast = useToast();
const isReserving = ref(false);
const isCheckoutModalOpen = ref(false);
const isFramingDialogOpen = ref(false);

const orderForm = ref({
  name: "",
  email: "",
  phone: "",
  pan: "",
  address: {
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "India",
  },
  hasGst: false,
  gstName: "",
  gstNumber: "",
  countryCode: "+91",
  countryIso: "IN",
  framing: "",
  framingSpecs: null as Record<string, any> | null,
  delivery_instructions: "",
});

const framingTotalCost = ref(0);

// Airtable stores this as the option name of "Framable/ Non-framable", so the casing it sends
// isn't guaranteed — compare case-insensitively rather than against one exact spelling.
const isNonFramable = computed(
  () => (artwork.value?.framable ?? "").toLowerCase() === "non-framable"
);

// Stretching onto bars, with no frame, is offered on canvas only. Already-framed work can take it
// too — the existing frame comes off and the canvas is mounted on bars instead — so it sits
// alongside keep-this-frame / choose-a-new-frame in that branch.
const canStretchArtwork = computed(
  () =>
    !isNonFramable.value &&
    (artwork.value?.artwork_category ?? "").trim() === "Canvas"
);

// Which build the buyer picked, for the calculator and the live preview.
const framingMode = computed<FramingMode | null>(() => {
  if (orderForm.value.framing === "Stretched") return "stretch";
  if (orderForm.value.framing === "Framed") return "frame";
  return null;
});

// "Existing" frame option: only for an already-framed piece that has framed photos. Display only —
// nothing extra is priced or stored beyond the framing label.
const framedArtworkImages = computed(() =>
  (artwork.value?.framed_artwork_images ?? "")
    .split(",")
    .map((name: string) => name.trim())
    .filter(Boolean),
);
// Needs: framable, marked as framed, framed photos AND a QAM framing/repair cost to charge for keeping the frame
const hasExistingFrameOption = computed(
  () =>
    !isNonFramable.value &&
    artwork.value?.is_work_framed === "Yes" &&
    framedArtworkImages.value.length > 0 &&
    String(artwork.value?.qam_framing_repair_cost ?? "").trim() !== "",
);
const isExistingDialogOpen = ref(false);
// Keeps the checkout from being dismissed by a tap inside one of its (teleported) dialogs. On touch screens the
// modal library settles a tap OUTSIDE it on the click that follows, so the guard is released a moment AFTER the
// dialog closes instead of the instant it closes.
const framingDialogGuard = ref(false);
let framingDialogGuardTimer: ReturnType<typeof setTimeout> | undefined;
watch([isFramingDialogOpen, isExistingDialogOpen], ([framingOpen, existingOpen]) => {
  if (framingDialogGuardTimer) clearTimeout(framingDialogGuardTimer);
  if (framingOpen || existingOpen) framingDialogGuard.value = true;
  else framingDialogGuardTimer = setTimeout(() => { framingDialogGuard.value = false; }, 400);
});
onBeforeUnmount(() => { if (framingDialogGuardTimer) clearTimeout(framingDialogGuardTimer); });

// Shown as the Cost in the existing-frame dialog (QAM framing/repair cost, without GST)
const existingFrameCostLabel = computed(() => formatFramingCost(artwork.value?.qam_framing_repair_cost));
// What choosing "Existing" adds to the order as framing: the same QAM framing/repair cost the dialog shows
const existingFrameCharge = computed(() => parseFramingCost(artwork.value?.qam_framing_repair_cost) ?? 0);
const customiseExisting = ref(false);
// True only once the buyer has gone through the customisation and pressed Done. Opening the framing dialog
// and closing it again does NOT count, so the notice asks them to "Complete customisation" instead.
const customisationDone = ref(false);
function onCustomisationDone() {
  customisationDone.value = true;
  closeFramingDialog();
}

function chooseExistingFrame() {
  customiseExisting.value = false;
  customisationDone.value = false;
  orderForm.value.framing = "Existing";
  orderForm.value.framingSpecs = null;
  framingTotalCost.value = existingFrameCharge.value;
  nextTick(() => {
    isExistingDialogOpen.value = false;
  });
}

function chooseCustomiseFrame() {
  customiseExisting.value = true;
  customisationDone.value = false;
  // Already on Framed: the calculator is still mounted and won't re-emit, so keep its specs and
  // price. Clearing them here is what left the preview without a frame.
  if (orderForm.value.framing !== "Framed") {
    orderForm.value.framing = "Framed";
    orderForm.value.framingSpecs = null;
    framingTotalCost.value = 0;
  }
  nextTick(() => {
    isExistingDialogOpen.value = false;
    // Go straight to the framing dialog (the mode watcher only fires when the mode changes)
    isFramingDialogOpen.value = true;
  });
}

// Closing checkout puts the customise path back to its starting state (existing-frame notice only),
// including any Unframed/Framed/Stretched pick made while customising. "Existing" is kept.
watch(isCheckoutModalOpen, (open) => {
  if (open || !customiseExisting.value) return;
  customiseExisting.value = false;
  customisationDone.value = false;
  isExistingDialogOpen.value = false;
  if (orderForm.value.framing !== "Existing") {
    orderForm.value.framing = "";
    orderForm.value.framingSpecs = null;
    framingTotalCost.value = 0;
  }
});

function closeFramingDialog() {
  nextTick(() => {
    isFramingDialogOpen.value = false;
  });
}

// Auto-open the framing configuration dialog the moment the buyer picks a build mode
// (Framed or Stretched). Closing the dialog does not clear the mode — the calculator
// stays mounted and preserves its selections until the buyer switches to Unframed.
// Re-opens on EVERY change to a real mode, not just from "no mode" — switching directly from
// Framed to Stretched (or back) goes mode-to-mode with no null in between, and the buyer still
// needs the dialog for whichever one they just picked.
watch(framingMode, (mode) => {
  if (mode) {
    // Defer by one tick so the checkbox click event fully propagates before the Teleport
    // backdrop renders. Without this, Nuxt UI's UModal detects a new outside element and
    // dismisses the checkout dialog on the same click that selected Framed/Stretched.
    nextTick(() => {
      isFramingDialogOpen.value = true;
    });
  } else {
    // Buyer switched to Unframed / no build: close and reset deferred by one tick so event propagation finishes.
    nextTick(() => {
      isFramingDialogOpen.value = false;
    });
  }
});

// The live preview sits in the checkout modal's left column rather than inline under each framing
// branch, so it needs the same visibility rule those two branches gave it: it shows only while a
// build is actually being configured — which both branches express through framingMode, since
// keep-this-frame is the one choice that sets no build — and once the calculator has emitted specs.
const showFramePreview = computed(() => {
  if (isNonFramable.value || !orderForm.value.framingSpecs) return false;
  return !!framingMode.value;
});

// Below lg the modal collapses to a single column, and a preview parked at the very top would sit
// above the checkout notes, far from what it reflects. There it moves in with the framing controls
// instead — right under the build choice, above the calculator. Matches the lg: prefix used on the
// modal layout below. Only one copy is ever mounted, so the visualizer always measures a container
// that is actually on screen.
const isPreviewBesideForm = useMediaQuery("(min-width: 1024px)");

// Shared by both placements, so the two call sites stay a single line each.
const framePreviewProps = computed(() => ({
  specifications: orderForm.value.framingSpecs,
  artworkLength: artworkLength.value,
  artworkHeight: artworkHeight.value,
  artworkImageSrc: getArtworkImageSrc(artwork.value?.photo_name ?? "", 720),
  photoWidth: artwork.value?.photo_width ?? undefined,
  photoHeight: artwork.value?.photo_height ?? undefined,
  mode: framingMode.value ?? "frame",
  unit: sizeUnit.value,
}));

// What actually gets stored on the order for framing — a clean, human-readable record built from
// the calculator's raw output (orderForm.framingSpecs), which stays untouched and keeps feeding
// the live preview and the calculator itself exactly as before. See ~/utils/framingOrderPayload.
const framingOrderPayload = computed<ArtworkFramingRecord | null>(() => {
  if (isNonFramable.value || !artwork.value) return null;
  const piece = buildPieceFraming({
    artworkId: artwork.value.id,
    artworkName: artwork.value.name,
    sizeLabel: artwork.value.display_size || artwork.value.size_in_inches || "",
    widthIn: artworkLength.value,
    heightIn: artworkHeight.value,
    artworkCategory: resolveFramingArtworkType(artwork.value.artwork_category),
    isAlreadyFramed: artwork.value.is_work_framed === "Yes",
    choice: orderForm.value.framing,
    rawSpecs: orderForm.value.framingSpecs,
  });
  return piece ? { scope: "artwork", ...piece } : null;
});

// --- SHIPPING AVAILABILITY ---
const {
  isChecking: isCheckingShipping,
  isAvailable: isShippingAvailable,
  shippingType,
  statusMessage: shippingStatusMessage,
  isDelhi,
  checkShipping,
  resetShipping,
} = useShippingAvailability();

const destinationStateName = computed(() => {
  return orderForm.value.address.state || '';
});

const pincodeMismatch = computed(() => {
  return validatePincodeWithState(orderForm.value.address.zip, destinationStateName.value);
});

const pincodeMismatchError = computed(() => {
  return pincodeMismatch.value.isValid ? '' : pincodeMismatch.value.error;
});

watch(
  [() => orderForm.value.address.zip, () => destinationStateName.value],
  ([newZip, newState]) => {
    if (newZip && newZip.length === 6) {
      const check = validatePincodeWithState(newZip, newState);
      if (!check.isValid) {
        errors.value.zip = check.error || 'Pincode does not match selected state';
        resetShipping();
        return;
      } else {
        if (errors.value.zip && errors.value.zip.includes('belongs to')) {
          delete errors.value.zip;
        }
      }
      checkShipping(newZip);
    } else {
      resetShipping();
    }
  },
  { immediate: true }
);

const parsedDimensions = computed(() => {
  return parseArtworkDimensions(artwork.value);
});
const artworkLength = computed(() => parsedDimensions.value.widthInches);
const artworkHeight = computed(() => parsedDimensions.value.heightInches);
const artworkDepth = computed(() => parsedDimensions.value.depthInches || 0);

// Whether the piece ships as a rigid build — only when the buyer explicitly chooses a frame
const isFramed = computed(() => {
  return (
    orderForm.value.framing === "Framed" ||
    orderForm.value.framing === "Stretched" ||
    orderForm.value.framing === "Keep this frame" ||
    !!framingMode.value ||
    !!orderForm.value.framingSpecs ||
    (typeof framingTotalCost.value === "number" && framingTotalCost.value > 0 && orderForm.value.framing !== "Existing")
  );
});

const frameDepth = computed(() => {
  const specs = orderForm.value.framingSpecs;
  if (specs) {
    if (framingMode.value === "stretch" && specs.stretcherBar) {
      return stretcherBarDepthInches(specs.stretcherBar);
    }
    if (typeof specs.frameDepth === "number") {
      return specs.frameDepth;
    }
    if (specs.frameDepth !== undefined && !isNaN(Number(specs.frameDepth))) {
      return Number(specs.frameDepth);
    }
    if (specs.depth !== undefined && !isNaN(Number(specs.depth))) {
      return Number(specs.depth);
    }
    if (specs.stretcherBar) {
      return stretcherBarDepthInches(specs.stretcherBar);
    }
  }
  if (orderForm.value.framing === "Keep this frame") {
    return 2;
  }
  return 0;
});

const calculatedShipping = computed(() => {
  if (!artwork.value) return null;
  const width = artworkLength.value || 0;
  const height = artworkHeight.value || 0;
  if (!width || !height) return null;

  const isDelhiLoc = Boolean(
    isDelhi?.value ||
    orderForm.value.address.zip?.startsWith('11') ||
    destinationStateName.value?.toLowerCase().includes('delhi')
  );

  return calculateShippingCost(
    {
      artworkWidth: width,
      artworkHeight: height,
      artworkDepth: artworkDepth.value,
      hasFrame: isFramed.value,
      selectedFrameDepth: frameDepth.value,
      declaredValueInr: artwork.value.listing_price || 0,
      actualWeightKg: parseWeightKg(artwork.value?.weight),
    },
    {
      destinationState: destinationStateName.value,
      isDelhi: isDelhiLoc,
    }
  );
});

// Online payment is India only: any other phone country code shows the International Orders note
// and keeps Pay disabled.
const isInternationalPhone = computed(() => orderForm.value.countryIso !== "IN");

const onPhoneCountryChange = (iso: string) => {
  const selected = countries.find((c) => c.iso === iso);
  orderForm.value.countryIso = iso;
  if (selected) orderForm.value.countryCode = selected.dialCode;
};

const shippingTotalCost = computed(() => {
  if (isShippingAvailable.value === false || !!pincodeMismatchError.value) {
    return 0;
  }
  return calculatedShipping.value?.totalShippingCost || 0;
});

const baseItemCost = computed(() => {
  return (artwork.value?.listing_price ?? 0) + framingTotalCost.value;
});

const gstAmount = computed(() => {
  return (baseItemCost.value + shippingTotalCost.value) * 0.05;
});

const totalOrderAmount = computed(() => {
  return baseItemCost.value + shippingTotalCost.value + gstAmount.value;
});



const errors = ref<Record<string, string>>({});

const { isChecking: isCheckingAvailability, check: checkAvailability } = useAvailabilityCheck();

const handleReserveClick = async () => {
  if (artwork.value?.id) {
    trackPurchaseCtaClicked({
      artwork_id: artwork.value.id,
      cta_label: "Buy Now",
      purchase_path_type: "buy_now",
      source_context: "artwork_detail",
    });
  }

  // If user is not signed in, redirect to sign-in page
  if (!isLoggedIn.value) {
    navigateTo({ path: '/signin', query: { redirect: route.fullPath } });
    return;
  }

  // Only start checkout if the artwork is still available right now (checked live, not from the page data)
  if (isCheckingAvailability.value) return;
  const availability = await checkAvailability({ artworkIds: artwork.value?.id ? [artwork.value.id] : [] });
  if (!availability.checked) {
    toast.add({ title: "Couldn't confirm availability", description: "Please check your connection and try again.", color: "error" });
    return;
  }
  if (!availability.ok) {
    const entry = artwork.value?.id ? availability.artworks[artwork.value.id] : undefined;
    // Flip the page to its Sold / Reserved state so the buyer sees what changed
    if (entry?.found && artwork.value) artwork.value = { ...artwork.value, availability: entry.availability };
    toast.add({ title: "No longer available", description: "This artwork is no longer available to buy.", color: "error" });
    return;
  }

  errors.value = {};
  // Reset form and prefill with logged-in user's data
  const meta = user.value?.user_metadata ?? {};
  const userName = profile.value?.name || meta.full_name || meta.display_name || '';
  const userEmail = user.value?.email || '';

  // Parse phone from profile (stored as "dialCode-number")
  // Only prefill if the country code is +91 (India)
  let userPhone = '';
  let userCountryCode = '+91';
  let userCountryIso = 'IN';
  if (profile.value?.phone) {
    const parts = profile.value.phone.split('-');
    if (parts.length === 2 && parts[0] === '+91') {
      userCountryCode = parts[0];
      userPhone = parts[1];
      userCountryIso = 'IN'; // We know it's IN for +91
    }
  }

  orderForm.value = {
    name: userName,
    email: userEmail,
    phone: userPhone,
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'India',
    },
    hasGst: false,
    gstName: '',
    gstNumber: '',
    countryCode: userCountryCode || '+91',
    countryIso: userCountryIso,
    framing: "",
    framingSpecs: null,
  };
  framingTotalCost.value = 0;
  isCheckoutModalOpen.value = true;
};

const handleAddToCart = () => {
  if (artwork.value?.id) {
    trackPurchaseCtaClicked({
      artwork_id: artwork.value.id,
      cta_label: isInCart.value ? "Added to Cart" : "Add to Cart",
      purchase_path_type: "add_to_cart",
      source_context: "artwork_detail",
    });
  }

  if (!isLoggedIn.value) {
    navigateTo({ path: '/signin', query: { redirect: route.fullPath } });
    return;
  }
  if (isInCart.value) {
    openCart();
  } else {
    addToCart({ artworkId: artwork.value?.id });
  }
};


const handleFinalPaymentTrigger = async () => {
  const f = orderForm.value;
  errors.value = {}; // Clear previous errors
  
  // 1. Basic Fields Validation
  if (!isNonFramable.value) {
    if (!f.framing) {
      errors.value.framing = "Please select a framing option";
    } else if (needsMountOrSideDepth(f.framingSpecs)) {
      errors.value.framing = "Please select a mount, a side depth, or both before continuing";
    }
  }
  if (!f.name) errors.value.name = "Full name is required";
  if (!f.email) errors.value.email = "Email address is required";
  if (!f.phone) errors.value.phone = "Phone number is required";
  if (!f.address.street) errors.value.street = "Street address is required";
  if (!f.address.city) errors.value.city = "City is required";
  if (!f.address.state) errors.value.state = "State is required";
  if (!f.address.zip) {
    errors.value.zip = "Zip code is required";
  } else if (f.address.zip.length !== 6) {
    errors.value.zip = "Zip code must be exactly 6 digits";
  } else {
    const mismatch = validatePincodeWithState(f.address.zip, destinationStateName.value);
    if (!mismatch.isValid) {
      errors.value.zip = mismatch.error || "Pincode does not match selected state";
    }
  }

  // 2. Email Validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (f.email && !emailRegex.test(f.email)) {
    errors.value.email = "Please enter a valid email address";
  }

  // 3. Phone Validation (Simple 10-digit check)
  if (!f.phone) {
    errors.value.phone = "Phone number is required";
  } else if (f.phone.length !== 10) {
    errors.value.phone = "Please enter a valid 10-digit Number";
  }

  // 4. GST Validation
  if (f.hasGst) {
    if (!f.gstName) errors.value.gstName = "Company name is required";
    if (!f.gstNumber) errors.value.gstNumber = "GSTIN is required";
  }

  // 5. Shipping Availability Validation
  if (isCheckingShipping.value) {
    return;
  }
  if (isInternationalPhone.value) {
    return;
  }
  if (isShippingAvailable.value === false) {
    errors.value.zip = shippingStatusMessage.value || "Shipping is currently unavailable for this pincode.";
    scrollToFirstError(errors.value);
    return;
  }

  // If there are any errors, stop
  if (Object.keys(errors.value).length > 0) {
    scrollToFirstError(errors.value);
    return;
  }

  isReserving.value = true;
  try {
    await processPayment({
      artworkId: artwork.value?.id as string,
      customerDetails: {
        name: f.name,
        email: f.email,
        phone: `${f.countryCode}${f.phone}`, // Combine for Razorpay
        pan: f.pan,
        hasGst: f.hasGst,
        gstName: f.hasGst ? f.gstName : "",
        gstNumber: f.hasGst ? f.gstNumber : "",
        framing: f.framing,
        framingCharges: framingTotalCost.value,
        framingSpecs: framingOrderPayload.value ?? undefined,
        // The exact props this checkout's own live preview is drawn from — stored as-is so a later
        // read-only render (e.g. the internal Sales Summary) can bind them straight to
        // FrameVisualizer with zero reconstruction, rather than reverse-engineering them from the
        // cleaned framingSpecs record above and risking a mismatch.
        framingPreview: showFramePreview.value ? framePreviewProps.value : null,
        shippingCharges: shippingTotalCost.value,
        shippingDetails: calculatedShipping.value || undefined,
        delivery_instructions: f.delivery_instructions || undefined,
      },

      shippingAddress: {
        ...f.address,
        state: f.address.state,
      },
    });

    isCheckoutModalOpen.value = false;
  } finally {
    isReserving.value = false;
  }
};



const startPayment = async () => {
  if (!artwork.value) return;
  
  await processPayment({
    artworkId: artwork.value.id,
    customerDetails: {
      name: orderInfo.value.name,
      email: orderInfo.value.email,
      phone: orderInfo.value.phone,
    },
    shippingAddress: orderInfo.value.address,
  });
  
  isReserveModalOpen.value = false;
};

const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});

const artworkId = computed(() => artwork.value?.id);

const { data: savesCount, refetch: refetchSavesCount } = useQuery({
  queryKey: computed(() => [`artwork-saves-${artworkId.value}`]),
  queryFn: async () => {
    if (!artworkId.value) return 0;
    const data = await $fetch<{ count: number }>(`/api/artwork-saves/${artworkId.value}`);
    return data.count;
  },
  enabled: computed(() => isMounted.value && !!artworkId.value),
  refetchOnWindowFocus: false,
});

const handleToggleSelection = async () => {
  if (artwork.value?.id) {
    trackArtworkSaved({
      artwork_id: artwork.value.id,
      artwork_slug: artwork.value.slug,
      list_context: "artwork_detail",
      detail_or_card: "detail",
      action: isArtworkSaved.value ? "unsaved" : "saved",
    });
  }
  await toggleSavingArtwork(artwork.value?.id, confirmRemoval);
};

const { globalUnit, formatDimensions } = useMeasurementUnit();
const sizeUnit = ref<'in' | 'cm'>(globalUnit.value);

watch(globalUnit, (newUnit) => {
  sizeUnit.value = newUnit;
});

const displaySize = computed(() => {
  if (!artwork.value?.display_size) return "";
  return formatDimensions(artwork.value.display_size, sizeUnit.value);
});

const isZoomOpen = ref(false);
const isImageRevealed = ref(false);
const isHiResLoaded = ref(false);

const thumbnailImageSrc = computed(() => {
  const photo = artwork.value?.photo_name || artwork.value?.photo_names?.[0];
  if (!photo) return "";
  return getArtworkImageSrc(photo, 360);
});

const hiResImageSrc = computed(() => {
  const photo = artwork.value?.photo_name || artwork.value?.photo_names?.[0];
  if (!photo) return "";
  return getArtworkImageSrc(photo, 1920);
});

// CRITICAL: During active view transition, lock activeImageSrc strictly to thumbnailImageSrc!
// Upgrade to 1920px only after:
// 1. 1920px image has finished downloading and decoding in memory, AND
// 2. View transition animation is NO LONGER active.
const activeImageSrc = computed(() => {
  if (isHiResLoaded.value && !isTransitionActive.value) {
    return hiResImageSrc.value;
  }
  return thumbnailImageSrc.value;
});

const loadHiResImage = () => {
  if (!import.meta.client) return;
  const url = hiResImageSrc.value;
  if (!url) return;

  const img = new Image();
  img.src = url;
  if (img.complete) {
    if (typeof img.decode === "function") {
      img.decode()
        .then(() => { isHiResLoaded.value = true; })
        .catch(() => { isHiResLoaded.value = true; });
    } else {
      isHiResLoaded.value = true;
    }
  } else {
    img.onload = async () => {
      try {
        if (typeof img.decode === "function") {
          await img.decode();
        }
      } catch (_) {}
      isHiResLoaded.value = true;
    };
  }
};

watch(() => artwork.value?.photo_name, loadHiResImage, { immediate: true });

watch(
  () => activeImageSrc.value,
  (src) => {
    if (import.meta.dev && src) {
      const is1920 = src.includes("/1920") || src.includes("1920");
      if (is1920) {
        console.log(`[Artwork Transition] hi-res upgrade (t=${Math.round(performance.now())}ms)`, { src: "1920px (hi-res)" });
      }
    }
  },
  { immediate: true }
);

function triggerImageReveal() {
  isImageRevealed.value = false;
  setTimeout(() => {
    requestAnimationFrame(() => {
      isImageRevealed.value = true;
    });
  }, 60);
}

onMounted(() => {
  if (import.meta.dev) {
    console.log(`[Artwork Transition] destination rendered (t=${Math.round(performance.now())}ms)`, {
      slug,
      hasPrefetched: !!prefetchedArtwork,
      isTransitionActive: isTransitionActive.value,
      activeSrc: activeImageSrc.value.includes("1920") ? "1920px" : "360px",
    });
  }
  if (isTransitionActive.value) {
    isImageRevealed.value = true;
  } else if (artwork.value) {
    triggerImageReveal();
  }
});

watch(artwork, (val) => {
  if (val && !isImageRevealed.value) {
    if (isTransitionActive.value) {
      isImageRevealed.value = true;
    } else {
      triggerImageReveal();
    }
  }
});
</script>

<template>
  <div
    class="main-container flex flex-1 flex-col gap-11 py-8 md:gap-16 md:py-16"
  >
    <div
      v-if="status === 'pending' && !artwork"
      class="flex items-center justify-center py-8 md:py-16"
    >
      <CustomLoader />
    </div>
    <template v-else-if="artwork">
      <div
        class="grid auto-rows-auto gap-8 sm:row-auto md:grid-cols-[1fr_1fr] md:gap-16 items-start"
      >
        <!-- Picture -->
        <div class="relative group cursor-zoom-in overflow-hidden md:sticky md:top-28 self-start" @click="isZoomOpen = true">
          <div
            class="mask-wipe-reveal w-full"
            :class="{
              'no-wipe': isTransitionActive,
              in: isImageRevealed || isTransitionActive
            }"
          >
            <img
              fetchpriority="high"
              decoding="async"
              class="max-h-100 w-full object-contain object-top-left md:max-h-[calc(100vh-9rem)] md:object-top transition-transform duration-300 group-hover:scale-[1.01]"
              :class="{ 'artwork-transition-target': isTransitionActive }"
              :style="getHeroTransitionStyle()"
              :src="activeImageSrc"
              :width="artwork.photo_width"
              :height="artwork.photo_height"
              :alt="`${artwork.name} by ${artwork.artist_name}${artwork.medium ? ` - ${artwork.medium}` : ''}`"
            />
          </div>
          <!-- Subtle Zoom Icon on Hover (Perfect circle white theme matching QAM styling) -->
          <div class="absolute bottom-4 right-4 flex items-center justify-center size-11 bg-white border border-on-primary-border text-on-primary-heading rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition duration-300 pointer-events-none">
            <Icon name="lucide:zoom-in" class="size-5" />
          </div>
        </div>

        <!-- Text information -->
        <div class="flex flex-col items-start gap-8 text-sm md:text-lg">
          <div>
            <h1 class="text-2xl font-bold leading-snug text-on-primary-heading sm:text-3xl lg:text-[38px] mb-6 text-balance">
              {{ artwork.name }}
            </h1>

            <p class="text-sm md:text-lg text-on-primary-body">
              by
              <CustomLink
                :to="`/artists/${artwork.artist_slug}`"
                :text="artwork.artist_name ?? ''"
                :disable-icon="true"
                class="font-light"
              />
            </p>
          </div>

          <div>
            <p v-if="artwork?.year">{{ artwork.year }}</p>
            <MediumFormattedText v-if="artwork?.medium" :text="artwork.medium" />
            <div v-if="artwork?.display_size" class="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2.5">
              <p class="shrink-0">{{ displaySize }}</p>

              <!-- Size Toggle & Unit Change Scope Popover -->
              <MeasurementUnitToggle v-model="sizeUnit" />
            </div>
          </div>

          <div class="flex w-full flex-col font-medium">
            <template v-if="artwork.listing_price && !UNAVAILABLE_ARTWORK_STATUSES.includes(artwork.availability)">
              <ArtworkPriceDisplay :price="artwork.listing_price" />

              <button
                type="button"
                @click="isBuybackOpen = !isBuybackOpen"
                :aria-expanded="isBuybackOpen"
                class="group inline-flex items-center gap-2 self-start border border-[#bfd2e0] bg-[#e6edf3] text-[#2c4f6d] hover:bg-[#dbe6ee] px-3.5 py-1.5 rounded-[6px] text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer mt-3 select-none ring-offset-1 focus-visible:outline-2 focus-visible:outline-[#3f6c93] buyback-pill-anim"
              >
                <Icon name="material-symbols:cycle" class="size-4 shrink-0 text-[#3f6c93]" />
                <span>Buyback protected</span>
                <Icon
                  name="material-symbols:keyboard-arrow-down-rounded"
                  class="size-4 shrink-0 transition-transform duration-300 ease-out"
                  :class="{ 'rotate-180': isBuybackOpen }"
                />
              </button>

              <div v-if="!artwork.availability" class="flex gap-4 mt-6 w-full">
                <Button 
                  class="flex-1 justify-center" 
                  variant="primary" 
                  :loading="isCheckingAvailability"
                  @click="handleReserveClick"
                >
                  Buy Now
                </Button>
                <Button 
                  class="flex-1 justify-center" 
                  variant="primary" 
                  :loading="isAdding"
                  @click="handleAddToCart"
                >
                  {{ isInCart ? 'Added to Cart' : 'Add to Cart' }}
                </Button>
              </div>
            </template>
            
            <div class="mt-4">
              <div v-if="SOLD_ARTWORK_STATUSES.includes(artwork.availability)" class="flex flex-col gap-3">
                <div class="w-fit bg-on-primary-body px-6 py-2 text-button-secondary rounded-[6px]">
                  Sold
                </div>
                <div class="flex flex-col md:flex-row items-stretch gap-2 md:gap-3 w-full">
                  <Button
                    :variant="isArtworkSaved ? 'primary' : 'secondary'"
                    @click="handleToggleSelection"
                    class="w-full md:w-auto md:flex-1 justify-center whitespace-nowrap px-3 md:px-4 text-xs sm:text-[13px] md:text-sm"
                  >
                    <HeartLikeButton
                      as="span"
                      :model-value="isArtworkSaved"
                      :size="18"
                      class="pointer-events-none shrink-0"
                    />
                    <span class="inline-grid [grid-template-areas:'stack'] items-center justify-items-start -ml-1">
                      <span class="[grid-area:stack] invisible pointer-events-none select-none" aria-hidden="true">Added to your selection</span>
                      <span class="[grid-area:stack] text-left">{{ isArtworkSaved ? "Added to your selection" : "Add to your selection" }}</span>
                    </span>
                  </Button>
                  <Button @click="share" class="w-full md:w-auto md:flex-1 justify-center whitespace-nowrap px-3.5 md:px-4 text-xs sm:text-[13px] md:text-sm">
                    <Icon name="material-symbols:ios-share" class="shrink-0" /> Share
                  </Button>
                </div>
              </div>
              <div v-else-if="artwork.availability === 'Reserved'" class="flex flex-col gap-3">
                <div class="w-fit bg-on-primary-body px-6 py-2 text-button-secondary rounded-[6px]">
                  Reserved
                </div>
                <div class="flex flex-col md:flex-row items-stretch gap-2 md:gap-3 w-full">
                  <Button
                    :variant="isArtworkSaved ? 'primary' : 'secondary'"
                    @click="handleToggleSelection"
                    class="w-full md:w-auto md:flex-1 justify-center whitespace-nowrap px-3 md:px-4 text-xs sm:text-[13px] md:text-sm"
                  >
                    <HeartLikeButton
                      as="span"
                      :model-value="isArtworkSaved"
                      :size="18"
                      class="pointer-events-none shrink-0"
                    />
                    <span class="inline-grid [grid-template-areas:'stack'] items-center justify-items-start -ml-1">
                      <span class="[grid-area:stack] invisible pointer-events-none select-none" aria-hidden="true">Added to your selection</span>
                      <span class="[grid-area:stack] text-left">{{ isArtworkSaved ? "Added to your selection" : "Add to your selection" }}</span>
                    </span>
                  </Button>
                  <Button @click="share" class="w-full md:w-auto md:flex-1 justify-center whitespace-nowrap px-3.5 md:px-4 text-xs sm:text-[13px] md:text-sm">
                    <Icon name="material-symbols:ios-share" class="shrink-0" /> Share
                  </Button>
                </div>
              </div>
              <PriceOnRequestForm
                v-else-if="artworkId"
                :artwork-id="artworkId"
                :artwork-url="canonicalUrl"
                :button-variant="artwork.listing_price && !artwork.availability ? 'secondary' : 'primary'"
                :has-price="!!artwork.listing_price"
                class="w-full"
              >
                <template #actions>
                  <Button
                    :variant="isArtworkSaved ? 'primary' : 'secondary'"
                    @click="handleToggleSelection"
                    class="w-full sm:flex-1 sm:min-w-[calc(50%-0.5rem)] xl:min-w-0 xl:flex-[1.15] justify-center whitespace-nowrap px-3 md:px-4 text-xs sm:text-[13px] md:text-sm"
                  >
                    <HeartLikeButton
                      as="span"
                      :model-value="isArtworkSaved"
                      :size="18"
                      class="pointer-events-none shrink-0"
                    />
                    <span class="inline-grid [grid-template-areas:'stack'] items-center justify-items-start -ml-1">
                      <span class="[grid-area:stack] invisible pointer-events-none select-none" aria-hidden="true">Added to your selection</span>
                      <span class="[grid-area:stack] text-left">{{ isArtworkSaved ? "Added to your selection" : "Add to your selection" }}</span>
                    </span>
                  </Button>
                  <Button
                    @click="share"
                    class="w-full xl:w-auto xl:flex-[0.85] justify-center whitespace-nowrap px-3.5 md:px-4 text-xs sm:text-[13px] md:text-sm"
                  >
                    <Icon name="material-symbols:ios-share" class="shrink-0" /> Share
                  </Button>
                </template>
              </PriceOnRequestForm>
            </div>
          </div>

          <div v-if="savesCount" class="flex items-center gap-1.5 text-sm md:text-base text-on-primary-body mt-2">
            <span class="leading-none shrink-0 inline-flex items-center">
              <HeartLikeButton
                as="span"
                :model-value="true"
                :size="16"
                class="pointer-events-none"
              />
            </span>
            <span>
              <span class="font-semibold text-on-primary-heading">{{ savesCount }}</span> {{ savesCount === 1 ? 'person has' : 'people have' }} added this artwork to their selection
            </span>
          </div>

          <CurrentlyOnView v-if="constellation" :constellation="constellation" class="mt-1" />

          <div v-if="artwork.artwork_text_on_website" class="flex flex-col gap-2">
            <h3 class="text-base font-bold text-on-primary-heading md:text-xl">
              A closer look:
            </h3>
            <div class="text-sm text-on-primary-body leading-relaxed md:text-base whitespace-pre-line text-balance">
              {{ artwork.artwork_text_on_website }}
            </div>
          </div>
        </div>
      </div>

      <!-- Collapsible Buyback Section (sits below hero grid) -->
      <div class="w-full">
        <BuybackSection
          :price="artwork.listing_price"
          v-model:open="isBuybackOpen"
          :collapsible="true"
          :show-pill="false"
        />
      </div>

      <SelectionRemovalConfirmationModal
        v-model:open="open"
        @confirm="onConfirm"
        @cancel="onCancel"
      />

      <ArtworkZoomModal
        v-model:open="isZoomOpen"
        :src="getArtworkImageSrc(artwork.photo_name ?? '', 1920)"
        :name="artwork.name"
        :artist="artwork.artist_name ?? ''"
      />

      <hr class="border-on-primary-body opacity-40" />

      <div class="flex flex-col gap-6 md:gap-8">
        <!-- Sort & Filter Bar with Title and Searchbar injected -->
        <div class="w-full">
          <ArtworkSortFilterBar
            :sort-by="artworkSortBy"
            :filters="artworkFilters"
            @update:sort-by="onArtworkSortByUpdate"
            @update:filters="onArtworkFiltersUpdate"
          >
            <template #controls-left>
              <SectionHeader class="flex-shrink-0 !leading-[0.75]"> More by this artist </SectionHeader>
            </template>

            <template #controls-prepend>
              <!-- Searchbar -->
              <div
                class="flex h-[38px] items-center gap-2 border-b border-on-primary-body pb-0.5 focus-within:border-on-primary-heading w-full md:w-64 md:mr-3"
              >
                <Icon name="lucide:search" class="shrink-0 size-4 text-on-primary-body" />
                <input
                  v-model="artworkSearchInput"
                  type="text"
                  placeholder="Search for an artwork"
                  class="text-sm font-medium text-on-primary-heading placeholder:text-on-primary-body focus:border-none focus:outline-none md:text-base w-full min-w-0 bg-transparent"
                />
              </div>
            </template>
          </ArtworkSortFilterBar>
        </div>

        <ArtworkFeed
          :artist-id="artwork.artist_id"
          :exclude-artwork-id="artwork.id"
          :search-query="debouncedArtworkSearch"
          :sort-by="artworkSortBy"
          :filters="artworkFilters"
        />
      </div>
    </template>
  </div>

  <!-- CHECKOUT & RESERVATION MODAL -->
  <!-- The dialog only needs the extra width while the live preview is on screen, so it widens when
       a frame is being configured and otherwise keeps Nuxt UI's default max-w-lg. Passing undefined
       (rather than restating max-w-lg) means the default is whatever the theme says it is. -->
  <UModal
    v-model:open="isCheckoutModalOpen"
    title="Checkout"
    :dismissible="!isReserving && !framingDialogGuard"
    :close="!isReserving && !framingDialogGuard"
    :ui="{ content: 'sm:max-w-lg' }"
  >
    <template #body>
      <div class="flex flex-col gap-6">

        <!-- Everything in a single column — framing is now configured in its own dialog -->
        <div class="flex flex-col gap-6 lg:flex-1 lg:min-w-0">
        <!-- Disclaimer Section -->
        <div class="flex flex-col gap-3">
          <div class="bg-gray-50 border-l-4 border-black p-4">
            <h4 class="font-bold text-sm mb-1 uppercase tracking-wider">Checkout</h4>
            <p class="text-xs text-gray-600 leading-relaxed">
              Complete the full payment to secure this artwork. 
              Within <strong>48 hours</strong>, our team will reach out to confirm your order, framing, and shipping details.
            </p>
          </div>

        </div>

        <!-- Artwork Framing. Shows the normal Unframed/Framed/Stretched choice regardless of
             is_work_framed — an already-framed piece used to get a separate "keep this frame /
             choose a new frame" question here, but it is asked like every other piece now. -->
        <div v-if="!isNonFramable" class="flex flex-col gap-4" data-error-field="framing">
          <h5 class="font-bold text-xs uppercase tracking-widest border-b pb-1">{{ hasExistingFrameOption ? 'This artwork is currently framed.' : 'Do you want the artwork to be framed?' }}</h5>
          <!-- Already-framed piece with photos: communicate the current frame instead of offering it as an option -->
          <div v-if="hasExistingFrameOption" class="flex flex-col gap-3 bg-gray-50 border border-gray-200 rounded-[8px] px-4 py-3">
            <p class="text-sm text-gray-800 leading-relaxed">
              Take a look at the existing frame, or explore customisation options to make it your own.
              Customisation may add to the cost and delivery time.
            </p>
            <div class="flex gap-3">
              <!-- After a choice is made the two buttons become one, doubling as the success message. Tapping it
                   reopens the existing-frame dialog, so the buyer can still change their mind. -->
              <!-- "Customised frame selected" only after the customisation was really finished (Done). If the buyer
                   opened the customisation and closed it again, the button says "Complete customisation" and takes them back. -->
              <Button
                v-if="orderForm.framing === 'Existing' || customiseExisting"
                size="sm"
                :variant="customiseExisting && !customisationDone ? 'primary' : 'secondary'"
                class="flex-1 justify-center"
                @click.prevent="customiseExisting && !customisationDone ? (isFramingDialogOpen = true) : (isExistingDialogOpen = true)"
              >
                <Icon v-if="!customiseExisting || customisationDone" name="lucide:check" class="w-4 h-4 text-green-600" />
                {{ customiseExisting ? (customisationDone ? 'Customised frame selected' : 'Complete customisation') : 'Current frame selected' }}
              </Button>
              <template v-else>
                <Button size="sm" class="flex-1 justify-center" variant="primary" @click.prevent="isExistingDialogOpen = true">See current frame</Button>
                <Button size="sm" class="flex-1 justify-center" variant="secondary" @click.prevent="chooseCustomiseFrame">I'll customise</Button>
              </template>
            </div>
          </div>
          <div v-if="!hasExistingFrameOption" class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer" @click.stop>
              <input type="checkbox" :checked="orderForm.framing === 'Unframed'" @change.stop="orderForm.framing = $event.target.checked ? 'Unframed' : ''; orderForm.framingSpecs = null; framingTotalCost = 0" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer">
              <span class="text-sm text-gray-700">Unframed</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer" @click.stop>
              <input type="checkbox" :checked="orderForm.framing === 'Framed'" @change.stop="orderForm.framing = $event.target.checked ? 'Framed' : ''" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer">
              <span class="text-sm text-gray-700">Framed</span>
            </label>
            <!-- Canvas only: mounted on stretcher bars and hung without a frame -->
            <label v-if="canStretchArtwork" class="flex items-center gap-2 cursor-pointer" @click.stop>
              <input type="checkbox" :checked="orderForm.framing === 'Stretched'" @change.stop="orderForm.framing = $event.target.checked ? 'Stretched' : ''" class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer">
              <span class="text-sm text-gray-700">Stretched</span>
            </label>
          </div>

          <!-- When a build mode is active, show a compact CTA that opens the framing dialog -->
          <div v-if="framingMode" :class="hasExistingFrameOption ? '' : 'mt-3'">
            <button
              type="button"
              @click.stop.prevent="isFramingDialogOpen = true"
              class="w-full flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 hover:border-gray-400 hover:bg-white transition-all duration-200 rounded-[8px] px-4 py-3 cursor-pointer group"
            >
              <div class="flex items-center gap-2.5">
                <Icon name="lucide:panel-left" class="w-4 h-4 text-gray-500 shrink-0 group-hover:text-gray-800 transition-colors" />
                <div class="flex flex-col items-start text-left">
                  <span class="text-[13px] font-semibold text-gray-900 leading-tight">
                    {{ orderForm.framingSpecs ? 'Framing configured' : 'Configure framing options' }}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span v-if="framingTotalCost > 0" class="text-sm font-bold text-gray-900">
                  + ₹{{ Math.round(framingTotalCost).toLocaleString('en-IN') }}
                </span>
                <span v-else-if="orderForm.framingSpecs" class="text-sm font-bold text-gray-900">-</span>
                <Icon name="lucide:arrow-right" class="w-4 h-4 text-gray-400 group-hover:text-gray-700 group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
            <!-- Full width, with room to actually explain — the inline "-" above just flags that
                 there's no number; this is where the buyer learns why. -->
            <p v-if="orderForm.framingSpecs && framingTotalCost <= 0" class="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2 leading-relaxed">
              ⚠ {{ framingMode === 'stretch' ? 'Stretching' : 'Framing' }} at this size is confirmed personally by our team, so it is
              <strong>not included in the amount payable now</strong>. Your selections are saved with the order and we
              will share the cost with you within 48 hours.
            </p>
          </div>
          <span v-if="errors.framing" class="text-red-500 text-xs mt-[-8px]">{{ errors.framing }}</span>
        </div>

        <!-- Personal Info -->
        <div class="flex flex-col gap-4">
          <h5 class="font-bold text-xs uppercase tracking-widest border-b pb-1">Personal Information</h5>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UFormField label="Full Name" data-error-field="name" :error="errors.name">
            <TextInput v-model="orderForm.name" placeholder="Enter your full name" :error="!!errors.name" />
          </UFormField>
          <UFormField label="Email Address" data-error-field="email" :error="errors.email">
            <TextInput v-model="orderForm.email" type="email" placeholder="your@email.com" :error="!!errors.email" />
          </UFormField>
          <UFormField label="Phone Number" data-error-field="phone" :error="errors.phone" class="w-full md:col-span-2">
            <div class="flex items-stretch gap-2 w-full">
              <CountryCodeSelect :model-value="orderForm.countryIso" @update:model-value="onPhoneCountryChange" />
              <div class="flex-1 min-w-0">
                <TextInput
                  v-model="orderForm.phone"
                  type="tel"
                  numeric-only
                  placeholder="Phone number"
                  :maxlength="isInternationalPhone ? 15 : 10"
                  :error="!!errors.phone"
                />
              </div>
            </div>
          </UFormField>
          <div v-if="isInternationalPhone" class="bg-blue-50/50 border-l-4 border-blue-400 p-4 md:col-span-2">
            <h4 class="font-bold text-sm mb-1 text-blue-800 uppercase tracking-wider">International Orders</h4>
            <p class="text-xs text-blue-700 leading-relaxed">
              We currently accept online orders for <strong>India only</strong>. For international inquiries, please reach out to us at 
              <a 
                target="_blank"
                :href="`https://mail.google.com/mail/?view=cm&fs=1&to=connect@quietart.com&su=International Inquiry: ${artwork?.name}&body=Hi Quiet Art Team,%0D%0A%0D%0AI am interested in the artwork '${artwork?.name}' and would like to inquire about international shipping and purchase.`" 
                class="font-bold underline underline-offset-2"
              >connect@quietart.com</a> 
              and our team will assist you personally.
            </p>
          </div>
        </div>
        </div>

        <!-- Shipping Address -->
        <AddressSelector 
          v-model="orderForm.address"
          v-model:errors="errors"
        />

        <!-- Blue Dart Shipping Availability Status -->
        <ShippingStatusBadge
          :is-checking="isCheckingShipping"
          :is-available="isShippingAvailable"
          :shipping-type="shippingType"
          :message="shippingStatusMessage"
          :pincode="orderForm.address.zip"
        />

        <!-- Special Delivery Instructions -->
        <div class="flex flex-col gap-4">
          <h5 class="font-bold text-xs uppercase tracking-widest border-b pb-1">Special Instructions</h5>
          <p class="text-sm text-gray-700">Please share if you have any special instructions for smoother delivery</p>
          <textarea
            v-model="orderForm.delivery_instructions"
            rows="3"
            placeholder="e.g. Suitable times, any alternate contact number for coordination, any landmarks for easier navigation…"
            class="w-full resize-none rounded-[6px] border-0 px-4 py-3 text-sm font-medium text-on-primary-heading ring ring-inset outline-none placeholder:text-on-primary-heading/40 focus-visible:ring-2 transition-colors"
          />
        </div>

        <!-- GST Section -->
        <div class="flex flex-col gap-4 bg-gray-50 p-4 border border-gray-100">
          <label class="flex items-center gap-3 cursor-pointer group">
            <input 
              type="checkbox" 
              v-model="orderForm.hasGst"
              class="w-4 h-4 rounded-none border border-gray-300 accent-black cursor-pointer"
            >
            <span class="text-sm text-gray-700">
              I need a GST Invoice
            </span>
          </label>


          <div v-if="orderForm.hasGst" class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <UFormField label="Name on GST Certificate" data-error-field="gstName" :error="errors.gstName">
              <TextInput v-model="orderForm.gstName" placeholder="Company Name" :error="!!errors.gstName" />
            </UFormField>
            <UFormField label="GSTIN" data-error-field="gstNumber" :error="errors.gstNumber">
              <TextInput v-model="orderForm.gstNumber" placeholder="22AAAAA0000A1Z5" class="uppercase" :error="!!errors.gstNumber" />
            </UFormField>
          </div>
        </div>

        <div class="flex flex-col gap-3 mt-2">
          <div class="flex justify-between text-sm text-gray-700">
            <span>Base Price:</span>
            <span>₹{{ Intl.NumberFormat("en-IN").format(artwork?.listing_price ?? 0) }}</span>
          </div>
          <div v-if="framingTotalCost > 0" class="flex justify-between text-sm text-gray-700">
            <span>Framing:</span>
            <span>₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(framingTotalCost) }}</span>
          </div>
          <div
            v-if="shippingTotalCost > 0 || isShippingAvailable === false || pincodeMismatchError"
            class="flex justify-between text-sm text-gray-700"
          >
            <span class="flex items-center gap-1.5">
              <span>Shipping:</span>
              <span v-if="isShippingAvailable === false || pincodeMismatchError" class="text-xs text-red-500 font-medium">
                Unavailable
              </span>
              <!-- <span v-else-if="calculatedShipping?.billableWeightKg" class="text-xs text-gray-500 font-normal">
                ({{ calculatedShipping.billableWeightKg }} kg billable)
              </span> -->
              <!-- <span
                v-if="canShowShippingBreakdown && isShippingAvailable !== false && !pincodeMismatchError"
                class="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-50 group-hover:bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded transition shadow-2xs"
                title="Click to view calculation breakdown (Dev/Staging)"
              >
                <Icon name="lucide:calculator" class="w-3 h-3" />
                <span>Details</span>
              </span> -->
            </span>
            <span v-if="isShippingAvailable === false || pincodeMismatchError" class="text-xs font-semibold text-red-600">
              Not Available
            </span>
            <span v-else>
              ₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(shippingTotalCost) }}
            </span>
          </div>
          <div class="flex justify-between text-sm text-gray-700">
            <span>GST (5%):</span>
            <span>₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(gstAmount) }}</span>
          </div>
          <div class="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2 pb-1">
            <span>Total:</span>
            <span>₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(totalOrderAmount) }}</span>
          </div>
          <Button 
            class="w-full justify-center py-4 mt-2" 
            variant="primary" 
            :loading="isReserving"
            :disabled="isReserving || isCheckingShipping || isInternationalPhone || isShippingAvailable === false || !!pincodeMismatchError"
            @click.stop.prevent="handleFinalPaymentTrigger"
          >
            Pay: ₹{{ Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(totalOrderAmount) }}
          </Button>
        </div>
        </div>
      </div>
    </template>
  </UModal>

  <!-- FRAMING CONFIGURATION DIALOG -->
  <!-- Rendered via Teleport so it sits above the checkout modal in the stacking context.
       v-if="framingMode" keeps the FramingCalculator in the DOM as long as the buyer
       has a build selected — preserving all their framing selections across open/close
       cycles. CSS visibility (not v-if) controls whether the overlay is actually shown. -->
  <Teleport to="body">
    <div
      v-if="framingMode"
      class="framing-overlay"
      :class="isFramingDialogOpen ? 'framing-overlay--visible' : 'framing-overlay--hidden'"
      aria-modal="true"
      role="dialog"
      aria-label="Configure framing"
    >
      <!-- Backdrop -->
      <Transition name="framing-fade">
        <div
          v-show="isFramingDialogOpen"
          class="framing-backdrop"
          @click.prevent="closeFramingDialog"
        />
      </Transition>

      <!-- Panel -->
      <Transition name="framing-slide">
        <div v-show="isFramingDialogOpen" class="framing-panel">

          <!-- Header -->
          <div class="framing-panel__header">
            <div>
              <h2 class="framing-panel__title">
                {{ framingMode === 'stretch' ? 'Stretching Options' : 'Framing Options' }}
              </h2>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              <MeasurementUnitToggle v-model="sizeUnit" placement="left" />
              <button
                type="button"
                @click.prevent="closeFramingDialog"
                class="framing-panel__close"
                aria-label="Close framing dialog"
              >
                <Icon name="lucide:x" class="w-4 h-4" />
              </button>
            </div>
          </div>

          <!-- Body: preview + calculator side by side -->
          <div class="framing-panel__body">

            <!-- LEFT: Live Frame Preview -->
            <div class="framing-panel__preview">
              <div class="framing-panel__preview-inner">
                <FrameVisualizer v-bind="framePreviewProps" />
              </div>
            </div>

            <!-- RIGHT: Framing Calculator -->
            <div class="framing-panel__calculator">
              <p class="framing-panel__section-label">
                {{ framingMode === 'stretch' ? 'Stretching' : 'Framing' }} Specifications
              </p>
              <FramingCalculator
                :artworkCategory="artwork.artwork_category || 'Canvas'"
                :initialLength="artworkLength"
                :initialHeight="artworkHeight"
                :sizeLabel="artwork.display_size || artwork.size_in_inches || ''"
                :mode="framingMode"
                :step-by-step="true"
                :unit="sizeUnit"
                @update:total="framingTotalCost = $event"
                @update:specifications="orderForm.framingSpecs = $event"
                @done="onCustomisationDone"
              />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>


  <!-- EXISTING FRAME DIALOG: shows the framed photos of the artwork -->
  <Teleport to="body">
    <div v-if="isExistingDialogOpen" class="framing-overlay framing-overlay--visible" aria-modal="true" role="dialog" aria-label="Existing frame">
      <div class="framing-backdrop" @click.prevent="isExistingDialogOpen = false" />
      <div class="framing-panel" style="max-width: 32rem; height: auto; max-height: 90dvh;">
        <div class="framing-panel__header">
          <h2 class="framing-panel__title">Take a look at the existing frame</h2>
          <button type="button" class="framing-panel__close" aria-label="Close" @click.prevent="isExistingDialogOpen = false">
            <Icon name="lucide:x" class="w-4 h-4" />
          </button>
        </div>
        <!-- Own wrapper (not .framing-panel__body, which turns into a row on desktop) -->
        <div class="flex flex-col gap-4 p-5 overflow-y-auto min-h-0">
          <div class="flex flex-col gap-3 items-center">
            <img
              v-for="name in framedArtworkImages"
              :key="name"
              :src="getFramedArtworkImageSrc(name)"
              :alt="`${artwork?.name} framed`"
              class="max-w-full max-h-[45vh] w-auto h-auto object-contain"
              loading="lazy"
            />
          </div>
          <div class="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-[8px] px-4 py-3">
            <span class="text-sm font-semibold text-gray-900">Cost</span>
            <span class="text-xl font-bold text-gray-900 leading-none">{{ existingFrameCostLabel }}</span>
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <Button class="flex-1 justify-center" variant="primary" @click.prevent="chooseExistingFrame">I'll go with this framing</Button>
            <Button class="flex-1 justify-center" variant="secondary" @click.prevent="chooseCustomiseFrame">I'll customise</Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- SHIPPING BREAKDOWN MODAL (DEV / STAGING ONLY) -->
  <!-- <ShippingBreakdownModal
    v-if="canShowShippingBreakdown"
    v-model:open="isShippingBreakdownOpen"
    :shipping="calculatedShipping"
    :artwork-name="artwork?.name"
    :destination-state="destinationStateName"
    :pincode="orderForm.address.zip"
  /> -->
</template>

<style scoped>
/* ─── Framing overlay wrapper ──────────────────────────────────────────────── */
.framing-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
@media (max-width: 1023px) {
  .framing-overlay {
    padding: 1rem;
    align-items: center;
  }
}
.framing-overlay--visible { pointer-events: auto; }
.framing-overlay--hidden  { pointer-events: none; }

/* ─── Backdrop ─────────────────────────────────────────────────────────────── */
.framing-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/* ─── Panel ────────────────────────────────────────────────────────────────── */
.framing-panel {
  position: relative;
  z-index: 10;
  background: var(--color-primary, #fff);
  width: 100%;
  max-width: 72rem;
  height: clamp(550px, 82dvh, 750px);
  max-height: 90dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.5rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.25),
    0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
}
@media (max-width: 1023px) {
  .framing-panel {
    height: 88dvh;
    max-height: 88dvh;
    border-radius: 0.5rem;
  }
}

/* ─── Header ───────────────────────────────────────────────────────────────── */
.framing-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
@media (max-width: 1023px) {
  .framing-panel__header {
    padding: 0.875rem 1rem;
  }
}
.framing-panel__title {
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-on-primary-heading, #111);
  margin: 0;
}
.framing-panel__subtitle {
  font-size: 0.8125rem;
  color: var(--color-on-primary-body, #555);
  margin: 0.25rem 0 0;
  line-height: 1.5;
}
@media (max-width: 639px) {
  .framing-panel__subtitle {
    font-size: 0.75rem;
    line-height: 1.35;
  }
}

/* Close — matches UModal's own close button: UButton color=neutral variant=ghost */
.framing-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  color: var(--color-on-primary-body, #6b7280);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
  padding: 0;
}
.framing-panel__close:hover {
  background: var(--ui-bg-elevated, rgba(0, 0, 0, 0.06));
  color: var(--color-on-primary-heading, #111);
}

/* ─── Body ─────────────────────────────────────────────────────────────────── */
.framing-panel__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
@media (min-width: 1024px) {
  .framing-panel__body {
    flex-direction: row;
    overflow-y: hidden;
  }
}

.framing-panel__section-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-on-primary-body, #888);
  margin: 0 0 0.75rem;
  flex-shrink: 0;
}
@media (max-width: 1023px) {
  .framing-panel__section-label {
    margin-bottom: 0.375rem;
  }
}

.framing-panel__preview {
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.5rem;
  /* Without this, a flex item's automatic minimum width is its CONTENT's natural size — and the
     frame inside renders at an explicit pixel width (outerWpx) that doesn't shrink on its own.
     That let this column (and the whole dialog with it) grow past its intended 45% whenever a
     bigger front-width/mount made that pixel width bigger, instead of the frame scaling down to
     fit. min-width: 0 is what actually makes "width: 45%" binding rather than a mere suggestion. */
  min-width: 0;
}
@media (max-width: 1023px) {
  .framing-panel__preview {
    padding: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    flex-shrink: 0;
    overflow: visible;
  }
}
@media (min-width: 1024px) {
  .framing-panel__preview {
    width: 45%;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    overflow-y: auto;
  }
}
/* Same reasoning as .framing-panel__preview's min-width: 0 above, one level deeper — without it,
   this div's own automatic minimum width is still FrameVisualizer's natural content size, which
   defeats the parent's min-width: 0 by just moving the leak down one level. overflow: hidden backs
   it up: a flex/block box with non-visible overflow gets an automatic minimum size of 0 by spec,
   so even a future layout change that drops the explicit min-width can't reopen this. */
.framing-panel__preview-inner {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.framing-panel__calculator {
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.5rem;
  min-width: 0;
}
@media (max-width: 1023px) {
  .framing-panel__calculator {
    padding: 1rem;
    flex-shrink: 0;
    overflow: visible;
  }
}
@media (min-width: 1024px) {
  .framing-panel__calculator {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
}

/* ─── Footer ───────────────────────────────────────────────────────────────── */
.framing-panel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: var(--color-button-secondary, #f9fafb);
  flex-shrink: 0;
}
@media (max-width: 1023px) {
  .framing-panel__footer {
    padding: 0.75rem 1rem;
  }
}
.framing-panel__footer-cost {
  font-size: 0.875rem;
  color: var(--color-on-primary-body, #555);
}
@media (max-width: 639px) {
  .framing-panel__footer-cost {
    font-size: 0.8125rem;
  }
}
.framing-panel__footer-cost strong {
  color: var(--color-on-primary-heading, #111);
}

/* ─── Transitions ──────────────────────────────────────────────────────────── */
.framing-fade-enter-active,
.framing-fade-leave-active { transition: opacity 0.25s ease; }
.framing-fade-enter-from,
.framing-fade-leave-to { opacity: 0; }

.framing-slide-enter-active {
  transition: opacity 0.25s ease, transform 0.28s ease-out;
}
.framing-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.framing-slide-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.98);
}
.framing-slide-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.99);
}
</style>
